'use client';

import { useToast } from '@/components/ui/use-toast';
import {
  fetchFormFieldsFromAPI,
  getBasket,
  getCheckoutState,
  refreshBasketFromAPI,
  setBasket,
  setCheckoutState,
} from '@/lib/BasketStorage';
import { APPOINTMENT_PRIMARY_CTA, isDecorationDeliveryBasket } from '@/lib/basketUtils';
import { CheckoutFormField } from '@/lib/CheckoutFormField';
import { CHECKOUT_V2_FLOW_KEY, CheckoutPage, CheckoutStepType, ReceiverInfoStep } from '@/lib/checkoutRoutes';
import { safeSessionStorage } from '@/lib/safeStorage';
import { startDistrictRemediation } from '@/lib/DistrictRemediationStorage';
import { Endpoints } from '@/lib/Endpoints';
import { ErrorHandler } from '@/lib/ErrorHandler';
import { reportCheckoutAError } from '@/lib/reportCheckoutBError';
import { pushCheckoutAbTest, pushMicroFunnels } from '@/lib/croCheckout';
import { sendGTMEvent } from '@/lib/gtm-wrapper';
import { AB_KEYS, markExperimentSeen } from '@/lib/abExperiments';
import { getBasketEcommerceBodyForProduct, GTMEventType } from '@/lib/GTMEventType';
import { getIsAnonymous, isAppClient } from '@/lib/LocalStorage';
import { appendCampaignParamsToUrl } from '@/lib/persistCampaignParams';
import { emailRegex, phoneRegex } from '@/lib/PhoneUtils';
import { Requester } from '@/lib/Requester';
import { syncSiteDistrictFromBasket, syncSiteDistrictFromDistrictId } from '@/lib/SiteDistrictSync';
import { formatBasketDeliveryDate, isTrue, isValidFullName } from '@/lib/utils';
import { WebLink } from '@/lib/WebLinks';
import { WindowDispatchEvent } from '@/lib/WindowDispatchEvent';
import { AddressValidation, Basket, BasketResponseModel } from '@/models/Basket';
import { CheckoutState } from '@/models/CheckoutState';
import { GenericResponse } from '@/models/GenericResponse';
import { PRODUCT_TYPE } from '@/models/Product';
import { ProductCardMessageContentModel } from '@/models/ProductCardMessage';
import clsx from 'clsx';
import dynamic from 'next/dynamic';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import EmptyBasketHomeDialog from './EmptyBasketHomeDialog';
import AddressDistrictMismatchModal from './modals/AddressDistrictMismatchModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Spinner } from './ui/spinner';
const DynamicCheckoutStepBillInfo = dynamic(() => import('./CheckoutStepBillInfo'), {
  ssr: false,
});
const DynamicCheckoutStepPaymentInfo = dynamic(() => import('./CheckoutStepPaymentInfo'), {
  ssr: false,
});
const DynamicCheckoutStepReceiverInfo = dynamic(() => import('./CheckoutStepReceiverInfo'), {
  ssr: false,
});
const DynamicCheckoutHistory = dynamic(() => import('./CheckoutHistory'), {
  ssr: false,
});
const DynamicCheckoutStepper = dynamic(() => import('./CheckoutStepper'), {
  ssr: false,
});

interface PageStateTypes {
  activeStep: CheckoutStepType;
  receiverInfoStep: ReceiverInfoStep;
  basket?: Basket;
  formFields?: {
    [key: string]: string;
  };
  cardMessages: ProductCardMessageContentModel[];
  videoMessage?: {
    key: string | null;
    thumbnailBase64: string | null;
  } | null;
  isLoading: boolean;
  addressMismatchValidation?: AddressValidation | null;
  deliverySlotUnavailableOpen?: boolean;
  remediationTargetDistrictId?: number | null;
  remediationTargetDistrictTitle?: string | null;
  addressFocusKey?: number;
}

const PAGE_STATE = {
  activeStep: CheckoutStepType.RECEIVER_INFO,
  receiverInfoStep: ReceiverInfoStep.RECEIVER_INFO,
  basket: undefined,
  cardMessages: [],
  videoMessage: { key: null, thumbnailBase64: null },
  formFields: {},
  isLoading: false,
  addressMismatchValidation: null,
  deliverySlotUnavailableOpen: false,
  remediationTargetDistrictId: null,
  remediationTargetDistrictTitle: null,
  addressFocusKey: 0,
} as PageStateTypes;

function initialPageState(
  forceActiveStep?: number,
  forceReceiverInfoStep?: number,
): PageStateTypes {
  const decoration = typeof window !== 'undefined' && isDecorationDeliveryBasket(getBasket());
  if (decoration && forceActiveStep === CheckoutStepType.RECEIVER_INFO) {
    return {
      ...PAGE_STATE,
      activeStep: CheckoutStepType.BILL_INFO,
      receiverInfoStep: ReceiverInfoStep.RECEIVER_INFO,
    };
  }
  return {
    ...PAGE_STATE,
    ...(forceActiveStep ? { activeStep: forceActiveStep } : {}),
    ...(forceReceiverInfoStep && !decoration ? { receiverInfoStep: forceReceiverInfoStep } : {}),
  };
}

export default function CheckoutContent({
  className,
  forceActiveStep,
  forceReceiverInfoStep,
  ...props
}: {
  className: string;
  forceActiveStep?: number;
  forceReceiverInfoStep?: number;
}) {
  const [pageState, setPageState] = useState(() =>
    initialPageState(forceActiveStep, forceReceiverInfoStep),
  );
  const [showPage, setShowPage] = useState(false);
  const [emptyHomeOpen, setEmptyHomeOpen] = useState(false);
  const hadBasketItems = useRef(false);
  const { toast } = useToast();
  const router = useRouter();
  const pathname = usePathname();
  const [showNextButton, setShowNextButton] = useState(false);
  const [isUserAnonymous, setUserIsAnonymous] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pageIsApp, setPageIsApp] = useState(false);
  const skipLandingUrlSync = useRef(true);
  const suppressUrlSync = useRef(false);
  const receiverStepRef = useRef(pageState.receiverInfoStep);
  receiverStepRef.current = pageState.receiverInfoStep;

  useEffect(() => {
    safeSessionStorage.removeItem(CHECKOUT_V2_FLOW_KEY);
    pushCheckoutAbTest('control');
  }, []);

  const totalSafe = pageState.basket?.total ?? getBasket()?.total;

  const saveCheckoutState = (newState: Partial<PageStateTypes>) => {
    const stateToSave: CheckoutState = {
      orderNumber: newState.basket?.orderNumber ?? pageState.basket?.orderNumber ?? null,
      activeStep: newState.activeStep ?? pageState.activeStep,
      receiverInfoStep: newState.receiverInfoStep ?? pageState.receiverInfoStep,
      formFields: newState.formFields ?? pageState.formFields,
      cardMessages: newState.cardMessages ?? pageState.cardMessages,
    };
    stateToSave.videoMessage = newState.videoMessage ?? pageState.videoMessage ?? null;
    setCheckoutState(stateToSave);
  };

  async function addProductToBasket(productId: number) {
    if (pageState.isLoading) return;
    setPageState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    try {
      const oldBasket = pageState.basket;
      if (!oldBasket) {
        return;
      }

      const body = isDecorationDeliveryBasket(oldBasket)
        ? {
            productId,
            warehouseId: oldBasket.district.id,
            date: oldBasket.deliveryTime.date,
            deliveryTimeId: oldBasket.deliveryTime.id,
            forceAdd: 0,
          }
        : {
            productId,
            districtId: oldBasket.district.id,
            date: oldBasket.deliveryTime.date,
            deliveryTimeId: oldBasket.deliveryTime.id,
            forceAdd: 0,
          };

      const { data: result } = await Requester.post<GenericResponse<BasketResponseModel>>(
        Endpoints.BASKET_ADD_ITEM,
        body,
      );

      if (result.data.basket) {
        sendGTMEvent({
          event: GTMEventType.ADD_TO_CART,
          ecommerce: getBasketEcommerceBodyForProduct(
            result.data.basket.items.filter((item) => item.id === productId),
            result.data.basket.total,
          ),
        });

        await refreshBasketFromAPI('CheckoutContent - addProductToBasket');
      }
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      reportCheckoutAError({
        step: 'extras',
        action: 'add-extra',
        kind: 'api',
        message,
        error,
      });
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setPageState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }

  async function removeProductFromBasket(productId: number) {
    if (pageState.isLoading) return;
    setPageState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    try {
      const body = {
        productId,
      };
      const currentBasket = getBasket();
      const { data: result } = await Requester.post<GenericResponse<BasketResponseModel>>(
        Endpoints.BASKET_REMOVE_ITEM,
        body,
      );

      sendGTMEvent({
        event: GTMEventType.REMOVE_FROM_CART,
        ecommerce: getBasketEcommerceBodyForProduct(
          currentBasket?.items.filter((item) => item.id === productId) || [],
          result.data.basket?.total || 0,
        ),
      });

      await refreshBasketFromAPI('CheckoutContent - removeProductFromBasket');
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      reportCheckoutAError({
        step: 'basket',
        action: 'change-count',
        kind: 'api',
        message,
        error,
      });
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setPageState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }

  async function increaseProduct(productId: number) {
    if (pageState.isLoading) return;
    setPageState((prev) => ({
      ...prev,
      isLoading: true,
    }));
    try {
      const body = {
        productId,
      };

      const { data: result } = await Requester.post<GenericResponse<BasketResponseModel>>(
        Endpoints.BASKET_INCREASE_ITEM,
        body,
      );

      sendGTMEvent({
        event: GTMEventType.ADD_TO_CART,
        ecommerce: getBasketEcommerceBodyForProduct(
          result.data.basket?.items.filter((item) => item.id === productId) || [],
          result.data.basket?.total || 0,
        ),
      });

      await refreshBasketFromAPI('CheckoutContent - increaseProduct');
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      reportCheckoutAError({
        step: 'basket',
        action: 'change-count',
        kind: 'api',
        message,
        error,
      });
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setPageState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }

  async function decreaseProduct(productId: number) {
    if (pageState.isLoading) return;
    setPageState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    try {
      const body = {
        productId,
      };
      const currentBasket = getBasket();
      const { data: result } = await Requester.post<GenericResponse<BasketResponseModel>>(
        Endpoints.BASKET_DECREASE_ITEM,
        body,
      );

      sendGTMEvent({
        event: GTMEventType.REMOVE_FROM_CART,
        ecommerce: getBasketEcommerceBodyForProduct(
          currentBasket?.items.filter((item) => item.id === productId) || [],
          result.data.basket?.total || 0,
        ),
      });

      await refreshBasketFromAPI('CheckoutContent - decreaseProduct');
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      reportCheckoutAError({
        step: 'basket',
        action: 'change-count',
        kind: 'api',
        message,
        error,
      });
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setPageState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }

  useEffect(() => {
    if (!isInitialized) {
      const localCheckoutState = getCheckoutState();
      const localBasket = getBasket();
      const basketOrderNumber = localBasket?.orderNumber ?? null;
      const stateOrderNumber = localCheckoutState?.orderNumber ?? null;
      const isOrderMismatch =
        basketOrderNumber && (!stateOrderNumber || stateOrderNumber !== basketOrderNumber);

      if (localCheckoutState && !isOrderMismatch) {
        setPageState((prev) => ({
          ...prev,
          formFields: {
            ...localCheckoutState.formFields,
          },
          cardMessages: localCheckoutState.cardMessages || [],
          videoMessage: localCheckoutState.videoMessage ?? { key: null, thumbnailBase64: null },
        }));
      } else if (isOrderMismatch) {
        setCheckoutState(undefined);
      }
      setIsInitialized(true);
    }
  }, [isInitialized]);

  useEffect(() => {
    setPageIsApp(isAppClient());
    const anonymousStateListener = () => {
      setUserIsAnonymous(getIsAnonymous());
    };

    window.addEventListener(WindowDispatchEvent.UPDATE_ANONYMOUS_STATE, anonymousStateListener);
    setUserIsAnonymous(getIsAnonymous());

    return () => {
      window.removeEventListener(
        WindowDispatchEvent.UPDATE_ANONYMOUS_STATE,
        anonymousStateListener,
      );
    };
  }, []);

  useEffect(() => {
    const b = getBasket();
    const decoration = isDecorationDeliveryBasket(b);

    if (decoration && forceActiveStep === CheckoutStepType.RECEIVER_INFO) {
      setPageState((prev) => ({
        ...prev,
        activeStep: CheckoutStepType.BILL_INFO,
        receiverInfoStep: ReceiverInfoStep.RECEIVER_INFO,
      }));
    } else if (forceActiveStep) {
      setPageState((prev) => ({
        ...prev,
        activeStep: forceActiveStep,
      }));
    }

    if (forceReceiverInfoStep && !decoration) {
      setPageState((prev) => ({
        ...prev,
        receiverInfoStep: forceReceiverInfoStep,
      }));
    }
    setShowPage(true);
  }, []);

  useEffect(() => {
    setShowNextButton(
      !(
        pageState.activeStep === CheckoutStepType.PAYMENT_INFO ||
        (pageState.activeStep === CheckoutStepType.BILL_INFO && isUserAnonymous)
      ),
    );
  }, [pageState.activeStep, isUserAnonymous]);

  const updateUrl = (url: CheckoutPage) => {
    if (pathname === `/${CheckoutPage.CHECKOUT_BASE_PAGE}` || pathname === `/${url}`) {
      router.replace(appendCampaignParamsToUrl(`/${url}`));
    } else {
      router.push(appendCampaignParamsToUrl(`/${url}`));
    }
  };

  const checkReceiverInfoWithFields = (formFields: { [key: string]: string }): boolean => {
    if (isDecorationDeliveryBasket(pageState.basket)) {
      return true;
    }

    if (!formFields || Object.keys(formFields).length === 0) {
      return false;
    }

    if (
      !formFields[CheckoutFormField.RECEIVER_FULL_NAME] ||
      formFields[CheckoutFormField.RECEIVER_FULL_NAME].length < 3
    ) {
      return false;
    }

    if (
      !formFields[CheckoutFormField.RECEIVER_PHONE] ||
      !formFields[CheckoutFormField.RECEIVER_PHONE].replace(/\D/g, '').match(phoneRegex)
    ) {
      return false;
    }

    if (
      !formFields[CheckoutFormField.RECEIVER_ADDRESS] ||
      formFields[CheckoutFormField.RECEIVER_ADDRESS].length < 14
    ) {
      return false;
    }

    if (pageState.basket?.district.deliveryWithCargo) {
      if (
        !formFields[CheckoutFormField.RECEIVER_CITY] ||
        formFields[CheckoutFormField.RECEIVER_CITY].length < 2
      ) {
        return false;
      }

      if (
        !formFields[CheckoutFormField.RECEIVER_DISTRICT] ||
        formFields[CheckoutFormField.RECEIVER_DISTRICT].length < 2
      ) {
        return false;
      }

      if (
        !formFields[CheckoutFormField.RECEIVER_NEIGHBORHOOD] ||
        formFields[CheckoutFormField.RECEIVER_NEIGHBORHOOD].length < 2
      ) {
        return false;
      }
    }

    return true;
  };

  const checkBillInfoWithFields = (formFields: { [key: string]: string }): boolean => {
    if (!formFields || Object.keys(formFields).length === 0) {
      return false;
    }

    if (
      !formFields[CheckoutFormField.SENDER_FULL_NAME] ||
      formFields[CheckoutFormField.SENDER_FULL_NAME].length < 3
    ) {
      return false;
    }

    if (!isValidFullName(formFields[CheckoutFormField.SENDER_FULL_NAME])) {
      return false;
    }

    if (
      !formFields[CheckoutFormField.SENDER_PHONE] ||
      !formFields[CheckoutFormField.SENDER_PHONE].replace(/\D/g, '').match(phoneRegex)
    ) {
      return false;
    }

    if (
      !formFields[CheckoutFormField.SENDER_MAIL] ||
      !formFields[CheckoutFormField.SENDER_MAIL].match(emailRegex)
    ) {
      return false;
    }

    if (isTrue(formFields[CheckoutFormField.INVOICE_REQUESTED])) {
      if (isTrue(formFields[CheckoutFormField.COMPANY_NAMED_INVOICE])) {
        if (
          !formFields[CheckoutFormField.INVOICE_TITLE] ||
          formFields[CheckoutFormField.INVOICE_TITLE].length < 3
        ) {
          return false;
        }

        if (
          !formFields[CheckoutFormField.TAX_OFFICE] ||
          formFields[CheckoutFormField.TAX_OFFICE].length < 3
        ) {
          return false;
        }

        if (
          !formFields[CheckoutFormField.TAX_NUMBER] ||
          !formFields[CheckoutFormField.TAX_NUMBER].match(/^[0-9]{10}$/)
        ) {
          return false;
        }
      }
    }

    return true;
  };

  const checkFormData = async (): Promise<boolean> => {
    if (!isInitialized) {
      return true;
    }

    let needsReceiverInfo = false;
    let needsBillInfo = false;

    if (pageState.activeStep === CheckoutStepType.RECEIVER_INFO) {
      if (pageState.receiverInfoStep !== ReceiverInfoStep.RECEIVER_INFO) {
        needsReceiverInfo = true;
      }
    } else if (pageState.activeStep === CheckoutStepType.BILL_INFO) {
      needsReceiverInfo = true;
    } else if (pageState.activeStep === CheckoutStepType.PAYMENT_INFO) {
      needsReceiverInfo = true;
      needsBillInfo = true;
    }

    const currentFormFields = pageState.formFields || {};

    if (needsReceiverInfo) {
      const receiverValid = checkReceiverInfoWithFields(currentFormFields);

      if (!receiverValid) {
        try {
          const apiFormFields = await fetchFormFieldsFromAPI();

          if (apiFormFields) {
            const mergedFields = { ...currentFormFields, ...apiFormFields };

            setPageState((prev) => ({
              ...prev,
              formFields: mergedFields,
            }));

            const receiverValidAfterAPI = checkReceiverInfoWithFields(mergedFields);

            if (!receiverValidAfterAPI) {
              setTimeout(() => {
                router.push(appendCampaignParamsToUrl(`/${CheckoutPage.CHECKOUT_RECEIVER_PAGE}`));
              }, 100);
              return false;
            }
          } else {
            setTimeout(() => {
              router.push(appendCampaignParamsToUrl(`/${CheckoutPage.CHECKOUT_RECEIVER_PAGE}`));
            }, 100);
            return false;
          }
        } catch (error) {
          console.error('API form field fetch error:', error);
          setTimeout(() => {
            router.push(appendCampaignParamsToUrl(`/${CheckoutPage.CHECKOUT_RECEIVER_PAGE}`));
          }, 100);
          return false;
        }
      }
    }

    if (needsBillInfo) {
      const currentFields = pageState.formFields || {};
      const billValid = checkBillInfoWithFields(currentFields);

      if (!billValid) {
        try {
          const apiFormFields = await fetchFormFieldsFromAPI();

          if (apiFormFields) {
            const mergedFields = { ...currentFields, ...apiFormFields };

            setPageState((prev) => ({
              ...prev,
              formFields: mergedFields,
            }));

            const billValidAfterAPI = checkBillInfoWithFields(mergedFields);

            if (!billValidAfterAPI) {
              setTimeout(() => {
                router.push(appendCampaignParamsToUrl(`/${CheckoutPage.CHECKOUT_INVOICE_PAGE}`));
              }, 100);
              return false;
            }
          } else {
            setTimeout(() => {
              router.push(appendCampaignParamsToUrl(`/${CheckoutPage.CHECKOUT_INVOICE_PAGE}`));
            }, 100);
            return false;
          }
        } catch (error) {
          console.error('API form field fetch error:', error);
          setTimeout(() => {
            router.push(appendCampaignParamsToUrl(`/${CheckoutPage.CHECKOUT_INVOICE_PAGE}`));
          }, 100);
          return false;
        }
      }
    }

    return true;
  };

  useEffect(() => {
    if (!isInitialized) return;
    if (!pageState.activeStep) return;

    // Opening URL is the source of truth on land. Default RECEIVER_INFO + this
    // sync used to replace /satin-al-ekstra-urunler before forceReceiverInfoStep applied.
    if (skipLandingUrlSync.current) {
      skipLandingUrlSync.current = false;
      return;
    }

    const b = pageState.basket ?? getBasket();
    if (!b || !Array.isArray((b as any).items) || (b as any).items.length === 0) return;

    let isCancelled = false;

    const handleFormCheck = async () => {
      try {
        const formValid = await checkFormData();

        if (isCancelled) return;

        if (!formValid) {
          suppressUrlSync.current = false;
          return;
        }

        if (suppressUrlSync.current) {
          if (pathname === `/${CheckoutPage.CHECKOUT_MESSAGE_PAGE}`) {
            suppressUrlSync.current = false;
          }
          window.scrollTo(0, 0);
          return;
        }

        let targetUrl: CheckoutPage | undefined;

        if (pageState.activeStep === CheckoutStepType.BILL_INFO) {
          targetUrl = CheckoutPage.CHECKOUT_INVOICE_PAGE;
        } else if (pageState.activeStep === CheckoutStepType.PAYMENT_INFO) {
          targetUrl = CheckoutPage.CHECKOUT_PAYMENT_PAGE;
        } else if (pageState.activeStep === CheckoutStepType.RECEIVER_INFO) {
          if (pageState.receiverInfoStep === ReceiverInfoStep.CARD_MESSAGES) {
            targetUrl = CheckoutPage.CHECKOUT_MESSAGE_PAGE;
          } else if (pageState.receiverInfoStep === ReceiverInfoStep.EXTRA_PRODUCT) {
            targetUrl = CheckoutPage.CHECKOUT_EXTRA_PAGE;
          } else if (pageState.receiverInfoStep === ReceiverInfoStep.RECEIVER_INFO) {
            targetUrl = CheckoutPage.CHECKOUT_RECEIVER_PAGE;
          }
        }

        if (targetUrl && pathname !== `/${targetUrl}`) {
          updateUrl(targetUrl);
        }

        window.scrollTo(0, 0);
      } catch (error) {
        console.error('Form check error:', error);
      }
    };

    const timeoutId = setTimeout(handleFormCheck, 100);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [pageState.activeStep, pageState.receiverInfoStep, isInitialized, pathname]);

  const isBasketShapeValid = (b?: Basket | null) => !!b && Array.isArray(b.items);

  const isBasketCheckoutEligible = (b?: Basket | null) =>
    !!b && Array.isArray(b.items) && b.items.length > 0;

  const basketListener = () => {
    setNewBasket(getBasket());
  };

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      let b = getBasket();
      if (!isBasketShapeValid(b)) {
        b = await refreshBasketFromAPI('CheckoutContent - init');
      }
      if (cancelled) return;

      if (!isBasketCheckoutEligible(b)) {
        if (pathname !== '/') {
          router.replace(appendCampaignParamsToUrl('/'));
        }
        return;
      }
      setNewBasket(b);
      markExperimentSeen(AB_KEYS.checkout);
    };

    init();

    window.addEventListener(WindowDispatchEvent.UPDATE_BASKET, basketListener);
    return () => {
      cancelled = true;
      window.removeEventListener(WindowDispatchEvent.UPDATE_BASKET, basketListener);
    };
  }, [router, pathname]);

  function leaveEmptyCheckout() {
    if (pathname !== '/') {
      router.replace(appendCampaignParamsToUrl('/'));
    }
  }

  async function setNewBasket(basket: Basket | null | undefined) {
    if (!basket || !basket.items || !Array.isArray(basket.items) || basket.items.length === 0) {
      setPageState((prev) => ({
        ...prev,
        basket: undefined,
        cardMessages: [],
        videoMessage: { key: null, thumbnailBase64: null },
      }));
      setCheckoutState(undefined);
      if (hadBasketItems.current) {
        setEmptyHomeOpen(true);
        return;
      }
      leaveEmptyCheckout();
      return;
    }
    hadBasketItems.current = true;

    const localCheckoutState = getCheckoutState();
    const basketOrderNumber = basket.orderNumber ?? null;
    const stateOrderNumber = localCheckoutState?.orderNumber ?? null;
    const isOrderMismatch =
      basketOrderNumber && (!stateOrderNumber || stateOrderNumber !== basketOrderNumber);
    const checkoutStateToUse = isOrderMismatch ? undefined : localCheckoutState;
    if (isOrderMismatch) {
      setCheckoutState(undefined);
    }
    const cardMessages: ProductCardMessageContentModel[] = [];

    try {
      basket.items.forEach((item) => {
        if (!item || typeof item.count !== 'number') {
          return;
        }

        for (let i = 0; i < item.count; i++) {
          if (!item?.isExtra && item.productTypeId !== PRODUCT_TYPE.PHOTO_PRINT) {
            const existingMessage = checkoutStateToUse?.cardMessages?.find(
              (msg) => msg.productId === item.id && msg.product?.count === item.count,
            );

            const dbSprayText1 = item.messageInformation?.[i]?.sprayText1;
            const dbSprayText2 = item.messageInformation?.[i]?.sprayText2;
            const dbContent = item.messageInformation?.[i]?.content;
            const dbMessageId = item.messageInformation?.[i]?.messageId;
            const dbCategoryId = item.messageInformation?.[i]?.categoryId;
            const dbIsAnonymous = item.messageInformation?.[i]?.isAnonymous;
            const dbSenderTitle = item.messageInformation?.[i]?.senderTitle;

            cardMessages.push({
              product: item,
              productId: item.id,
              content: dbContent || existingMessage?.content || '',
              messageId: dbMessageId || existingMessage?.messageId || 0,
              categoryId: dbCategoryId || existingMessage?.categoryId || undefined,
              isAnonymous: dbIsAnonymous || existingMessage?.isAnonymous || 0,
              senderTitle: dbSenderTitle || existingMessage?.senderTitle || '',
              sprayText1: dbSprayText1 || existingMessage?.sprayText1 || undefined,
              sprayText2: dbSprayText2 || existingMessage?.sprayText2 || undefined,
            } as ProductCardMessageContentModel);
          }
        }
      });
    } catch (error) {
      setPageState((prev) => ({
        ...prev,
        basket: undefined,
        cardMessages: [],
        videoMessage: { key: null, thumbnailBase64: null },
      }));
      return;
    }

    const decoration = isDecorationDeliveryBasket(basket);
    const path = typeof window !== 'undefined' ? window.location.pathname.replace(/^\//, '') : '';
    const onPaymentCheckoutPath = path === CheckoutPage.CHECKOUT_PAYMENT_PAGE;

    setPageState((prev) => {
      let nextActiveStep = prev.activeStep;
      let nextReceiverStep = checkoutStateToUse?.receiverInfoStep ?? prev.receiverInfoStep;

      if (decoration) {
        if (
          onPaymentCheckoutPath ||
          checkoutStateToUse?.activeStep === CheckoutStepType.PAYMENT_INFO
        ) {
          nextActiveStep = CheckoutStepType.PAYMENT_INFO;
        } else {
          nextActiveStep = CheckoutStepType.BILL_INFO;
        }
        nextReceiverStep = ReceiverInfoStep.RECEIVER_INFO;
      }

      const newState = {
        ...prev,
        basket: basket,
        cardMessages: cardMessages,
        videoMessage: checkoutStateToUse?.videoMessage ?? { key: null, thumbnailBase64: null },
        formFields: checkoutStateToUse?.formFields ?? (isOrderMismatch ? {} : prev.formFields),
        ...(decoration ? { activeStep: nextActiveStep, receiverInfoStep: nextReceiverStep } : {}),
      };

      saveCheckoutState(newState);
      return newState;
    });
  }

  async function onProductCardMessageUpdate(
    index: number,
    cardMessage: ProductCardMessageContentModel,
  ) {
    setPageState((prev) => {
      const newState = {
        ...prev,
        cardMessages: prev.cardMessages.map((item, mapIndex) =>
          mapIndex === index ? cardMessage : item,
        ),
      };

      saveCheckoutState(newState);

      return newState;
    });
  }

  function onVideoMessageChange(message: { key: string | null; thumbnailBase64: string | null }) {
    setPageState((prev) => {
      const newState = {
        ...prev,
        videoMessage: message,
      };

      saveCheckoutState(newState);
      return newState;
    });
  }

  function skipExtraProductStep() {
    if (receiverStepRef.current !== ReceiverInfoStep.EXTRA_PRODUCT) return;
    suppressUrlSync.current = true;
    router.replace(appendCampaignParamsToUrl(`/${CheckoutPage.CHECKOUT_MESSAGE_PAGE}`));
    setPageState((prev) => {
      if (prev.receiverInfoStep !== ReceiverInfoStep.EXTRA_PRODUCT) return prev;
      const newState = {
        ...prev,
        receiverInfoStep: ReceiverInfoStep.CARD_MESSAGES,
      };
      saveCheckoutState(newState);
      return newState;
    });
  }

  async function onNextClick() {
    switch (pageState.activeStep) {
      case CheckoutStepType.RECEIVER_INFO:
        await handleReceiverPageUpdate();
        break;

      case CheckoutStepType.BILL_INFO:
        await updateBillInfo();
        break;

      case CheckoutStepType.PAYMENT_INFO:
        break;
    }
  }

  async function handleReceiverPageUpdate() {
    switch (pageState.receiverInfoStep) {
      case ReceiverInfoStep.RECEIVER_INFO:
        await updateReceiverInfo();
        break;

      case ReceiverInfoStep.EXTRA_PRODUCT:
        setPageState((prev) => {
          const newState = {
            ...prev,
            receiverInfoStep: ReceiverInfoStep.CARD_MESSAGES,
          };
          saveCheckoutState(newState);
          return newState;
        });
        break;

      case ReceiverInfoStep.CARD_MESSAGES:
        await updateCardMessages();
        break;
    }
  }

  async function updateCardMessages() {
    if (pageState.isLoading) {
      return;
    }
    setPageState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    try {
      const body = {
        videoMessageKey: pageState.videoMessage?.key || null,
        messages: pageState.cardMessages.map((message) => ({
          productId: message.productId,
          messageId: message.messageId,
          content: message.content,
          isAnonymous: message.isAnonymous,
          senderTitle: message.senderTitle,
          sprayText1: message.sprayText1,
          sprayText2: message.sprayText2,
        })),
      };

      const { data: result } = await Requester.post<GenericResponse<BasketResponseModel>>(
        Endpoints.BASKET_UPDATE_MESSAGE,
        body,
      );

      setPageState((prev) => {
        const newState = {
          ...prev,
          activeStep: CheckoutStepType.BILL_INFO,
          receiverInfoStep: ReceiverInfoStep.EXTRA_PRODUCT,
        };
        saveCheckoutState(newState);
        return newState;
      });
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      reportCheckoutAError({
        step: 'card',
        action: 'save-message',
        kind: 'api',
        message,
        error,
      });
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setPageState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }

  const checkReceiverInfo = (): boolean => {
    if (isDecorationDeliveryBasket(pageState.basket)) {
      return true;
    }

    const formFields = pageState.formFields;
    if (!formFields || !isInitialized) {
      return false;
    }
    const receiverGaps: { action: string; description: string }[] = [];
    if (
      !formFields[CheckoutFormField.RECEIVER_FULL_NAME] ||
      formFields[CheckoutFormField.RECEIVER_FULL_NAME].length < 3
    ) {
      receiverGaps.push({
        action: 'Kime Gönderiyorsun - Alıcı adı',
        description: 'Alıcı isim alanı boş bırakılamaz.',
      });
    }
    if (
      !formFields[CheckoutFormField.RECEIVER_PHONE] ||
      !formFields[CheckoutFormField.RECEIVER_PHONE].replace(/\D/g, '').match(phoneRegex)
    ) {
      receiverGaps.push({
        action: 'Kime Gönderiyorsun - Telefon',
        description: 'Alıcı telefon numarası alanı boş bırakılamaz.',
      });
    }
    if (pageState.basket?.district.deliveryWithCargo) {
      if (
        !formFields[CheckoutFormField.RECEIVER_CITY] ||
        formFields[CheckoutFormField.RECEIVER_CITY].length < 2
      ) {
        receiverGaps.push({
          action: 'Kime Gönderiyorsun - İl',
          description: 'Lütfen alıcı ili seçin.',
        });
      }
      if (
        !formFields[CheckoutFormField.RECEIVER_DISTRICT] ||
        formFields[CheckoutFormField.RECEIVER_DISTRICT].length < 2
      ) {
        receiverGaps.push({
          action: 'Kime Gönderiyorsun - İlçe',
          description: 'Lütfen alıcı ilçesi seçin.',
        });
      }
      if (
        !formFields[CheckoutFormField.RECEIVER_NEIGHBORHOOD] ||
        formFields[CheckoutFormField.RECEIVER_NEIGHBORHOOD].length < 2
      ) {
        receiverGaps.push({
          action: 'Kime Gönderiyorsun - Mahalle',
          description: 'Lütfen alıcı mahallesi seçin.',
        });
      }
    }
    if (
      !formFields[CheckoutFormField.RECEIVER_ADDRESS] ||
      formFields[CheckoutFormField.RECEIVER_ADDRESS].length < 14
    ) {
      receiverGaps.push({
        action: 'Kime Gönderiyorsun - Adres',
        description: 'Alıcı adresini girdiğinizden emin olun.',
      });
    }
    if (receiverGaps.length > 0) {
      pushMicroFunnels(receiverGaps.map((gap) => ({ action: gap.action, label: 'Eksik Doldurma' })));
      toast({
        variant: 'destructive',
        description: receiverGaps[0].description,
      });
      return false;
    }

    if (pageState.isLoading) {
      return false;
    }

    return true;
  };

  function proceedToReceiverExtraStep() {
    setPageState((prev) => {
      const newState = {
        ...prev,
        receiverInfoStep: ReceiverInfoStep.EXTRA_PRODUCT,
        addressMismatchValidation: null,
        deliverySlotUnavailableOpen: false,
      };
      saveCheckoutState(newState);
      return newState;
    });
  }

  function buildReceiverRequestBody(formFields: { [key: string]: string }, districtId?: number) {
    return {
      title: formFields[CheckoutFormField.RECEIVER_FULL_NAME],
      city: formFields[CheckoutFormField.RECEIVER_CITY],
      district: formFields[CheckoutFormField.RECEIVER_DISTRICT],
      neighborhood: formFields[CheckoutFormField.RECEIVER_NEIGHBORHOOD],
      address: formFields[CheckoutFormField.RECEIVER_ADDRESS],
      phoneNumber: formFields[CheckoutFormField.RECEIVER_PHONE].replace(/\D/g, ''),
      orderNote: formFields[CheckoutFormField.RECEIVER_NOTE],
      companyName: formFields[CheckoutFormField.RECEIVER_COMPANY],
      ...(districtId ? { districtId } : {}),
    };
  }

  function resolveDistrictTitle(
    validation: AddressValidation | null | undefined,
    districtId: number,
  ) {
    if (!validation) return 'Seçilen mahalle';
    if (validation.nearestDistrictId === districtId && validation.nearestDistrictTitle) {
      return validation.nearestDistrictTitle;
    }
    const fromSuggested = validation.suggestedDistricts?.find((item) => item.id === districtId);
    if (fromSuggested?.title) return fromSuggested.title;
    const fromCandidates = validation.nearestCandidates?.find((item) => item.id === districtId);
    if (fromCandidates?.title) return fromCandidates.title;
    return validation.nearestDistrictTitle || 'Seçilen mahalle';
  }

  async function clearAllBasketItems(basket: Basket) {
    const productIds = [...new Set((basket.items || []).map((item) => item.id))];
    for (const productId of productIds) {
      await Requester.post<GenericResponse<BasketResponseModel>>(Endpoints.BASKET_REMOVE_ITEM, {
        productId,
      });
    }
    await refreshBasketFromAPI('CheckoutContent - clearAllBasketItems');
  }

  async function startBasketRemediationFlow({
    basket,
    targetDistrictId,
    targetDistrictTitle,
  }: {
    basket: Basket;
    targetDistrictId: number;
    targetDistrictTitle: string;
  }) {
    syncSiteDistrictFromDistrictId(targetDistrictId);
    startDistrictRemediation({
      basket,
      targetDistrictId,
      targetDistrictTitle,
    });
    await clearAllBasketItems(basket);
    setBasket(undefined, 'CheckoutContent - district remediation');
    setPageState((prev) => ({
      ...prev,
      addressMismatchValidation: null,
      deliverySlotUnavailableOpen: false,
      isLoading: false,
    }));
    if (typeof document !== 'undefined') {
      document.body.style.pointerEvents = '';
      document.body.style.overflow = '';
      document.body.removeAttribute('data-scroll-locked');
    }
    router.push(appendCampaignParamsToUrl(WebLink.CART));
  }

  async function submitReceiverUpdate(districtId?: number) {
    const formFields = pageState.formFields;
    if (!formFields) {
      return null;
    }

    const { data: result } = await Requester.post<GenericResponse<BasketResponseModel>>(
      Endpoints.BASKET_UPDATE_RECEIVER,
      buildReceiverRequestBody(formFields, districtId),
    );

    if (result.data.basket) {
      setBasket(result.data.basket, 'CheckoutContent - updateReceiverInfo');
      setPageState((prev) => ({
        ...prev,
        basket: result.data.basket,
      }));
      if (result.data.districtUpdated) {
        syncSiteDistrictFromBasket(result.data.basket);
      }
    }

    return result.data;
  }

  async function handleReceiverUpdateResult(
    data: BasketResponseModel,
    selectedDistrictId?: number,
  ) {
    const validation = data.addressValidation;
    const status = validation?.status;

    if (data.deliverySlotUnavailable && data.targetDistrictId != null && pageState.basket) {
      const targetDistrictId = data.targetDistrictId;
      setPageState((prev) => ({
        ...prev,
        isLoading: false,
        addressMismatchValidation: null,
        deliverySlotUnavailableOpen: true,
        remediationTargetDistrictId: targetDistrictId,
        remediationTargetDistrictTitle: resolveDistrictTitle(validation, targetDistrictId),
      }));
      return;
    }

    if (status === 'ok' || status === 'skipped') {
      proceedToReceiverExtraStep();
      return;
    }

    if (status === 'mismatch' || status === 'warning') {
      if (selectedDistrictId) {
        await startBasketRemediationFlow({
          basket: pageState.basket || getBasket()!,
          targetDistrictId: selectedDistrictId,
          targetDistrictTitle: resolveDistrictTitle(validation, selectedDistrictId),
        });
        return;
      }

      setPageState((prev) => ({
        ...prev,
        isLoading: false,
        addressMismatchValidation: validation || null,
      }));
      return;
    }

    proceedToReceiverExtraStep();
  }

  async function updateReceiverInfo(districtId?: number) {
    if (isDecorationDeliveryBasket(pageState.basket)) {
      setPageState((prev) => {
        const newState = {
          ...prev,
          receiverInfoStep: ReceiverInfoStep.EXTRA_PRODUCT,
        };
        saveCheckoutState(newState);
        return newState;
      });
      return;
    }

    if (!checkReceiverInfo()) {
      return;
    }

    const formFields = pageState.formFields;

    if (!formFields) {
      return;
    }

    setPageState((prev) => ({
      ...prev,
      isLoading: true,
      addressMismatchValidation: districtId ? prev.addressMismatchValidation : null,
    }));

    try {
      const data = await submitReceiverUpdate(districtId);
      if (!data) return;
      await handleReceiverUpdateResult(data, districtId);
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      reportCheckoutAError({
        step: 'receiver',
        action: 'update-receiver',
        kind: 'api',
        message,
        error,
      });
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setPageState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }

  async function handleDistrictMismatchSelection(districtId: number) {
    if (pageState.isLoading) return;
    await updateReceiverInfo(districtId);
  }

  async function confirmDeliverySlotRemediation() {
    const basket = pageState.basket || getBasket();
    const targetDistrictId = pageState.remediationTargetDistrictId;
    if (!basket || !targetDistrictId) {
      setPageState((prev) => ({ ...prev, deliverySlotUnavailableOpen: false }));
      return;
    }

    setPageState((prev) => ({ ...prev, isLoading: true, deliverySlotUnavailableOpen: false }));
    try {
      await startBasketRemediationFlow({
        basket,
        targetDistrictId,
        targetDistrictTitle:
          pageState.remediationTargetDistrictTitle ||
          resolveDistrictTitle(pageState.addressMismatchValidation, targetDistrictId),
      });
    } finally {
      setPageState((prev) => ({ ...prev, isLoading: false }));
    }
  }

  function handleAddressMismatchUpdateAddress() {
    setPageState((prev) => ({
      ...prev,
      addressMismatchValidation: null,
      addressFocusKey: (prev.addressFocusKey || 0) + 1,
    }));
  }

  const checkBillInfo = (): boolean => {
    const formFields = pageState.formFields;
    if (!formFields) {
      toast({
        variant: 'destructive',
        description: 'Tüm alanları doldurunuz',
      });
      return false;
    }
    if (
      !formFields[CheckoutFormField.SENDER_FULL_NAME] ||
      formFields[CheckoutFormField.SENDER_FULL_NAME].length < 3
    ) {
      toast({
        variant: 'destructive',
        description: 'Gönderici isim alanı boş bırakılamaz.',
      });
      return false;
    }
    if (/\d/.test(formFields[CheckoutFormField.SENDER_FULL_NAME])) {
      toast({
        variant: 'destructive',
        description: 'Ad soyad alanı rakam içeremez.',
      });
      return false;
    }
    if (!isValidFullName(formFields[CheckoutFormField.SENDER_FULL_NAME])) {
      toast({
        variant: 'destructive',
        description: 'Lütfen ad ve soyadınızı birlikte giriniz.',
      });
      return false;
    }
    if (
      !formFields[CheckoutFormField.SENDER_PHONE] ||
      !formFields[CheckoutFormField.SENDER_PHONE].replace(/\D/g, '').match(phoneRegex)
    ) {
      toast({
        variant: 'destructive',
        description: 'Gönderici telefon numarası alanı boş bırakılamaz.',
      });
      return false;
    }

    if (
      !formFields[CheckoutFormField.SENDER_MAIL] ||
      !formFields[CheckoutFormField.SENDER_MAIL].match(emailRegex)
    ) {
      toast({
        variant: 'destructive',
        description: 'Gönderici email adresi alanı boş bırakılamaz.',
      });
      return false;
    }

    if (isTrue(formFields[CheckoutFormField.INVOICE_REQUESTED])) {
      if (isTrue(formFields[CheckoutFormField.COMPANY_NAMED_INVOICE])) {
        if (
          !formFields[CheckoutFormField.INVOICE_TITLE] ||
          formFields[CheckoutFormField.INVOICE_TITLE].length < 3
        ) {
          toast({
            variant: 'destructive',
            description: 'Fatura unvanı boş bırakılamaz.',
          });
          return false;
        }

        if (
          !formFields[CheckoutFormField.TAX_OFFICE] ||
          formFields[CheckoutFormField.TAX_OFFICE].length < 3
        ) {
          toast({
            variant: 'destructive',
            description: 'Vergi dairesi alanı boş bırakılamaz',
          });
          return false;
        }

        if (
          !formFields[CheckoutFormField.TAX_NUMBER] ||
          !formFields[CheckoutFormField.TAX_NUMBER].match(/^[0-9]{10}$/)
        ) {
          toast({
            variant: 'destructive',
            description: 'Vergi numarasını doğru bir şekilde giriniz.',
          });
          return false;
        }
      }
    }
    return true;
  };

  async function updateBillInfo() {
    if (!checkBillInfo()) {
      return;
    }

    const formFields = pageState.formFields;

    if (!formFields) {
      return;
    }

    if (pageState.isLoading) {
      return;
    }

    setPageState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    try {
      const body = {
        title: formFields[CheckoutFormField.SENDER_FULL_NAME].trim(),
        phoneNumber: formFields[CheckoutFormField.SENDER_PHONE].replace(/\D/g, ''),
        email: formFields[CheckoutFormField.SENDER_MAIL],
        invoiceRequested: isTrue(formFields[CheckoutFormField.INVOICE_REQUESTED]) ? 1 : 0,
      };

      if (isTrue(formFields[CheckoutFormField.INVOICE_REQUESTED])) {
        const invoiceInformation = {
          address: formFields[CheckoutFormField.INVOICE_ADDRESS],
        };

        if (isTrue(formFields[CheckoutFormField.COMPANY_NAMED_INVOICE])) {
          invoiceInformation['title'] = formFields[CheckoutFormField.INVOICE_TITLE];
          invoiceInformation['taxOffice'] = formFields[CheckoutFormField.TAX_OFFICE];
          invoiceInformation['taxNumber'] = formFields[CheckoutFormField.TAX_NUMBER];
        }

        body['invoiceInformation'] = invoiceInformation;
      }

      const { data: result } = await Requester.post<GenericResponse<BasketResponseModel>>(
        Endpoints.BASKET_UPDATE_SENDER,
        body,
      );

      setBasket(result.data.basket, 'CheckoutContext - updateBillInfo');

      setPageState((prev) => {
        const newState = {
          ...prev,
          activeStep: CheckoutStepType.PAYMENT_INFO,
        };
        saveCheckoutState(newState);
        return newState;
      });
    } catch (error) {
      const { message } = ErrorHandler.getMessage(error);
      reportCheckoutAError({
        step: 'bill',
        action: 'save-sender',
        kind: 'api',
        message,
        error,
      });
      toast({
        variant: 'destructive',
        description: message,
      });
    } finally {
      setPageState((prev) => ({
        ...prev,
        isLoading: false,
      }));
    }
  }

  if (!showPage) {
    return <></>;
  }

  const isAppointmentCheckout = isDecorationDeliveryBasket(pageState.basket);

  const receiverKey =
    `receiver-${pageState.basket?.orderNumber ?? 'no-basket'}-` +
    `${pageState.receiverInfoStep}-` +
    `${pageState.cardMessages.map((m) => `${m.productId}-${m.categoryId ?? 'x'}`).join('_')}`;

  return (
    <div className={`max-w-7xl mx-auto md:gap-16 gap-8 ${className}`} {...props}>
      <DynamicCheckoutStepper
        className="mx-6 flex flex-row gap-8 mt-4"
        selectedType={pageState.activeStep}
        receiverInfoStep={pageState.receiverInfoStep}
        pageIsApp={pageIsApp}
        decorationFlow={isAppointmentCheckout}
      />
      <div
        className={clsx(
          `mx-auto w-full justify-center md:flex md:space-x-6 md:px-0 px-2 ${pageState.activeStep === CheckoutStepType.BILL_INFO && isUserAnonymous ? 'pb-44' : 'pb-16'} md:pb-0`,
        )}
      >
        <div className="rounded-xl md:w-2/3">
          {
            {
              1: (
                <DynamicCheckoutStepReceiverInfo
                  key={receiverKey}
                  isUserAnonymous={isUserAnonymous}
                  onMessageUpdate={onProductCardMessageUpdate}
                  onVideoMessageChange={onVideoMessageChange}
                  videoMessage={pageState.videoMessage ?? { key: null, thumbnailBase64: null }}
                  cardMessages={pageState.cardMessages}
                  deliveryDate={
                    pageState.basket?.deliveryTime
                      ? formatBasketDeliveryDate(pageState.basket?.deliveryTime)
                      : ''
                  }
                  receiverInfoStep={pageState.receiverInfoStep}
                  formFields={pageState.formFields}
                  updateFormFields={(key, value) =>
                    setPageState((prev) => {
                      const formFields = { ...(prev.formFields || {}), [key]: value };
                      const newState = { ...prev, formFields };
                      saveCheckoutState(newState);
                      return newState;
                    })
                  }
                  increaseItem={increaseProduct}
                  decreaseItem={decreaseProduct}
                  removeItem={removeProductFromBasket}
                  addItem={addProductToBasket}
                  basket={pageState.basket}
                  pageIsApp={pageIsApp}
                  addressFocusKey={pageState.addressFocusKey}
                  onSkipExtraStep={skipExtraProductStep}
                />
              ),
              2: (
                <DynamicCheckoutStepBillInfo
                  formFields={pageState.formFields}
                  checkout={onNextClick}
                  updateFormFields={(key, value) =>
                    setPageState((prev) => {
                      const formFields = { ...(prev.formFields || {}), [key]: value };
                      const newState = { ...prev, formFields };
                      saveCheckoutState(newState);
                      return newState;
                    })
                  }
                />
              ),
              3: (
                <DynamicCheckoutStepPaymentInfo
                  orderNumber={pageState.basket?.orderNumber || ''}
                  total={totalSafe}
                  basket={pageState.basket}
                  isAppointmentCheckout={isAppointmentCheckout}
                />
              ),
            }[pageState.activeStep]
          }
        </div>

        <div className="flex flex-col md:w-1/3 gap-6">
          <div className="flex h-fit w-full justify-center p-4 sm:p-6 rounded-xl border bg-hzc_beige_400 order-2 sm:order-1">
            {pageState.basket && (
              <DynamicCheckoutHistory
                basket={pageState.basket}
                removeItem={removeProductFromBasket}
                increaseItem={increaseProduct}
                decreaseItem={decreaseProduct}
                activeStep={pageState.activeStep}
                loading={pageState.isLoading}
                isAppointmentFlow={isAppointmentCheckout}
              />
            )}
          </div>
          {showNextButton && (
            <Button
              className="sm:flex hidden rounded-full bg-hzc_purple_500 hover:bg-hzc_purple_500 hover:scale-105 transition-all duration-300 text-hzc_beige_100 order-1 sm:order-2 md:mx-0 mx-6"
              onClick={onNextClick}
            >
              {pageState.isLoading && <Spinner className="text-sm me-2" />}
              {isAppointmentCheckout ? APPOINTMENT_PRIMARY_CTA : 'İleri'}
            </Button>
          )}
          {showNextButton && (
            <div className="sm:hidden fixed bottom-0 left-0 right-0 flex flex-col gap-4 p-4 bg-hzc_purple_500 z-50">
              <Button
                className="text-hzc_beige_900 font-medium h-[48px] w-full rounded-full bg-[#D2F25C] "
                onClick={onNextClick}
              >
                {pageState.isLoading && <Spinner className="text-sm me-2" />}
                {isAppointmentCheckout ? APPOINTMENT_PRIMARY_CTA : 'Kaydet ve Devam Et'}
              </Button>
            </div>
          )}
        </div>
      </div>

      <EmptyBasketHomeDialog open={emptyHomeOpen} onHome={leaveEmptyCheckout} />

      <AddressDistrictMismatchModal
        open={Boolean(pageState.addressMismatchValidation)}
        validation={pageState.addressMismatchValidation || null}
        loading={pageState.isLoading}
        onSelectDistrict={handleDistrictMismatchSelection}
        onUpdateAddress={handleAddressMismatchUpdateAddress}
        onClose={() =>
          setPageState((prev) => ({
            ...prev,
            addressMismatchValidation: null,
          }))
        }
      />

      <AlertDialog
        open={Boolean(pageState.deliverySlotUnavailableOpen)}
        onOpenChange={(open) => {
          if (!open) {
            setPageState((prev) => ({ ...prev, deliverySlotUnavailableOpen: false }));
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Teslimat saati bu mahallede uygun değil</AlertDialogTitle>
            <AlertDialogDescription>
              Seçtiğiniz mahalle için mevcut teslimat günü ve saati kullanılamıyor. Sepetinize dönüp
              ürünlerinizi yeni mahalle ve aynı gün/saat ile yeniden eklemeniz gerekiyor.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={confirmDeliverySlotRemediation}>Tamam</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
